import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { type } from 'os';
import { apiServiceInstance } from '../apiUtils';
import { UsersSlice } from '../Reducers/loginReducer';
import {
  checkExistingUserObjProp,
  forgotPasswordObjProp,
  loginObjProp,
  productToursType,
  setUserStatusObjType,
  signUpUserObjProp,
} from '../Reducers/Types';

export const loginUser = createAsyncThunk(
  'loginUser',
  async (loginObj: loginObjProp) => {
    const res = await apiServiceInstance.post('/login/', loginObj);
    return res;
  },
);

type authProp = loginObjProp | null;

export const auth = createAsyncThunk(
  'login/auth',
  async (loginObj: authProp, { dispatch }) => {
    try {
      if (loginObj) {
        const userData = await dispatch(loginUser(loginObj)).unwrap();
        // if this succeeds, you’re guaranteed login worked
        const response = await apiServiceInstance.post('/auth/');
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = error?.message;
      return errorMessage; // or rethrow depending on your flow
    }
  },
);

export const authCheck = createAsyncThunk('authCheck', async () => {
  const response = await apiServiceInstance.post('/auth/');
  return response.data;
});

export const checkExistingUser = createAsyncThunk(
  'checkExistingUser',
  async (emailObj: checkExistingUserObjProp) => {
    const res = await apiServiceInstance.get(
      '/register/?email=' + emailObj.email,
    );
    return res;
  },
);

export const signUpUser = createAsyncThunk(
  'signUpUser',
  async (signObj: signUpUserObjProp) => {
    const res = await apiServiceInstance.post('/register/', signObj);
    return res;
  },
);

export const forgotPassword = createAsyncThunk(
  'forgotPassword',
  async (forgotPasswordObj: forgotPasswordObjProp) => {
    const res = await apiServiceInstance.post(
      '/password/reset',
      forgotPasswordObj,
    );
    return res;
  },
);

export type resetPasswordProp = {
  oldPassword: string;
  newPassword: string;
};

export const resetPassword = createAsyncThunk(
  'resetPassword',
  async (payload: resetPasswordProp) => {
    const res = await apiServiceInstance.post('/password/change', payload);
    return res;
  },
);

export type resetPasswordWithTokenProp = {
  password: string;
  token: string;
};

export const resetPasswordWithToken = async (
  payload: resetPasswordWithTokenProp,
) => {
  const res = await axios.put(
    '/password/reset',
    {
      password: payload.password,
    },
    {
      headers: {
        Authorization: `Bearer ${payload.token}`,
      },
    },
  );
  return res;
};

export const logoutUser = createAsyncThunk('logoutUser', async () => {
  const res = await apiServiceInstance.post('/logout/');
  return res;
});

export type updateProfileProp = {
  userName?: string;
  profilePicUrl?: string;
  user_id?: number;
};

export const updateProfile = createAsyncThunk(
  'updateProfile',
  async (updateProfile: updateProfileProp) => {
    await apiServiceInstance.put('/auth/profile', updateProfile);
    return updateProfile;
  },
);

export const getProductTours = createAsyncThunk('getProductTours', async () => {
  const res = await apiServiceInstance.get('/tour/');
  return res.data?.value;
});

export const addTourStatus = createAsyncThunk(
  'addTourStatus',
  async (payload: productToursType) => {
    await apiServiceInstance.post('/tour/', payload);
    return payload;
  },
);

export const setUserStatus = async (setUserStatusObj: setUserStatusObjType) => {
  const res = await apiServiceInstance.post(
    '/SetUserStatus/',
    setUserStatusObj,
  );
  return res;
};

export const getPolicyData = async (userId: number) => {
  const res = await apiServiceInstance.post('/auth/policy', { userId });
  return res;
};

export const agreePolicy = async (policyId: {
  policyId: number;
  userId: number;
}) => {
  const res = await apiServiceInstance.put('/auth/policy', {
    policyId: policyId.policyId,
    userId: policyId.userId,
  });
  return res;
};

// Azure AD authentication
export type azureAdAuthProp = {
  accessToken: string;
  refreshToken?: string;
  account: any;
  email?: string;
  name?: string;
};

export const azureAdAuth = createAsyncThunk(
  'auth/azureAd',
  async (payload: azureAdAuthProp) => {
    try {
      // Extract email and name from payload or account
      const email =
        payload.email ||
        payload.account?.username ||
        payload.account?.homeAccountId;
      const name = payload.name || payload.account?.name || '';

      // Send Azure AD token to backend for verification and user creation/login
      const response = await apiServiceInstance.post('/auth/azure/', {
        access_token: payload.accessToken,
        refresh_token: payload.refreshToken,
        email: email,
        name: name,
        OauthType: 'azure',
      });
      return response;
    } catch (error: any) {
      console.error('Azure AD backend error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Azure AD authentication failed';
      throw new Error(errorMessage);
    }
  },
);

// Samsara/Okta authentication
export type samsaraAuthProp = {
  accessToken: string;
  email: string;
  name?: string;
};

export const samsaraAuth = createAsyncThunk(
  'auth/samsara',
  async (payload: samsaraAuthProp) => {
    try {
      // Send Samsara/Okta token to backend for verification
      const response = await apiServiceInstance.post('/auth/azure/', {
        access_token: payload.accessToken,
        email: payload.email,
        name: payload.name || '',
        OauthType: 'samsara',
      });
      return response;
    } catch (error: any) {
      console.error('Samsara auth backend error:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Samsara authentication failed';
      throw new Error(errorMessage);
    }
  },
);
